// =============================================================================
// components/voice/ConnectionBanner.tsx — minimal connecting/reconnecting strip.
//
// Must be rendered INSIDE <LiveKitRoom> (uses useConnectionState against the
// room context). Shows nothing while connected; a slim glass banner with a
// spinner for connecting/reconnecting, and a red variant if the room reports
// disconnected while the screen is still up (the screens' leave flows navigate
// away, so this is only visible on an unexpected drop).
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useConnectionState } from '@livekit/react-native';
import { ConnectionState } from 'livekit-client';
import { colors, spacing, radius, fonts } from '../../constants/theme';

export default function ConnectionBanner({ topOffset = 0 }: { topOffset?: number }) {
  const state = useConnectionState();

  if (state === ConnectionState.Connected) return null;

  const isDropped = state === ConnectionState.Disconnected;
  const label =
    state === ConnectionState.Reconnecting || state === ConnectionState.SignalReconnecting
      ? 'Reconnecting…'
      : isDropped
        ? 'Connection lost'
        : 'Connecting…';

  return (
    <View pointerEvents="none" style={[styles.wrap, { top: topOffset + spacing.sm }]}>
      <View style={[styles.banner, isDropped && styles.bannerError]}>
        {!isDropped && <ActivityIndicator size="small" color={colors.white} />}
        <Text style={styles.text}>{label}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 50,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    backgroundColor: 'rgba(108,92,231,0.92)', // lavender glass over dark stage
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
  },
  bannerError: {
    backgroundColor: 'rgba(255,71,87,0.92)',
  },
  text: {
    fontSize: 13,
    color: colors.white,
    ...fonts.semibold,
  },
});
